/* eslint-disable @sa-nu/no-date */
import { Temporal } from "@js-temporal/polyfill";
import memoize from "memoize";

export type DateTimeString = string & { readonly brand: unique symbol };

export type ZonedDateTimeString = string & { readonly brand: unique symbol };
export type PlainDateString = string & { readonly brand: unique symbol };
export type PlainDateTimeString = string & { readonly brand: unique symbol };
export type PlainYearMonthString = string & { readonly brand: unique symbol };

export type PlainDate = Temporal.PlainDate;
export const PlainDate = Temporal.PlainDate;

export type PlainYearMonth = Temporal.PlainYearMonth;
export const PlainYearMonth = Temporal.PlainYearMonth;

export type PlainDateTime = Temporal.PlainDateTime;
export const PlainDateTime = Temporal.PlainDateTime;

export type ZonedDateTime = Temporal.ZonedDateTime;
export const ZonedDateTime = Temporal.ZonedDateTime;

let _overrideCurrentTime: ZonedDateTime | undefined;

/**
 * getCurrentTime() が返す時刻を上書きする（dev環境でのデバッグ用）。
 * 呼び出し側（webapp, core-system）が起動時に環境変数を読んで注入する。
 * @param time ZonedDateTime 文字列 (例: "2026-03-15T10:00:00+09:00[Asia/Tokyo]")。undefined で解除。
 */
export const setCurrentTimeOverride = (time: string | undefined) => {
  _overrideCurrentTime = time ? Temporal.ZonedDateTime.from(time) : undefined;
};

/** サービスの基準タイムゾーン。日付境界も営業時間もすべて JST で定義されている。 */
export const JST_TIME_ZONE = "Asia/Tokyo";

export const getCurrentTime = () => {
  if (_overrideCurrentTime) {
    return _overrideCurrentTime;
  }
  return Temporal.Now.instant().toZonedDateTimeISO(JST_TIME_ZONE);
};

export const getCurrentDate = () => {
  return getCurrentTime().toPlainDate();
};

export const getCurrentYearMonth = () => {
  return getCurrentTime().toPlainDate().toPlainYearMonth();
};

export const getCurrentDateTime = () => {
  return getCurrentTime().toPlainDateTime();
};

// 指定された年月が当月かどうかを判定
export const isCurrentYearMonth = (
  yearMonth: PlainYearMonth | PlainYearMonthString
): boolean => {
  const targetYearMonth =
    typeof yearMonth === "string" ? PlainYearMonth.from(yearMonth) : yearMonth;
  const currentYearMonth = getCurrentYearMonth();
  return PlainYearMonth.compare(targetYearMonth, currentYearMonth) === 0;
};

/// 月末日を取得する
/// 売上日の取得などに利用できる
export const getEndOfMonth = (date: PlainDate): PlainDate => {
  return date.add({ months: 1 }).with({ day: 1 }).subtract({ days: 1 });
};

export const getMaxTime = () =>
  ZonedDateTime.from("9999-12-31T23:59:59+00:00[UTC]");

// 端点を含む閉区間 [start, end] に date が入るか。
export function isDateInRange(
  date: PlainDate,
  start: PlainDate,
  end: PlainDate
): boolean {
  return (
    PlainDate.compare(date, start) >= 0 && PlainDate.compare(date, end) <= 0
  );
}

const _genPlainDateList = (
  rawFirstDate: PlainDate | PlainDateString,
  rawLastDate: PlainDate | PlainDateString
): PlainDate[] => {
  const firstDate =
    typeof rawFirstDate === "string"
      ? PlainDate.from(rawFirstDate)
      : rawFirstDate;
  const lastDate =
    typeof rawLastDate === "string" ? PlainDate.from(rawLastDate) : rawLastDate;
  const nights = firstDate.until(lastDate).total({ unit: "day" }) + 1;
  return Array.from({ length: nights }).map<PlainDate>((_, i) =>
    firstDate.add({ days: i })
  );
};

export const genPlainDateList = memoize(_genPlainDateList, {
  cacheKey: ([firstDate, lastDate]) => `${firstDate}..${lastDate}`,
});

export const genPlainYearMonthList = (
  rawFirstDate: PlainDate | PlainDateString,
  rawLastDate: PlainDate | PlainDateString
): PlainYearMonth[] => {
  const firstDate =
    typeof rawFirstDate === "string"
      ? PlainDate.from(rawFirstDate)
      : rawFirstDate;
  const lastDate =
    typeof rawLastDate === "string" ? PlainDate.from(rawLastDate) : rawLastDate;
  const firstMonth = firstDate.toPlainYearMonth();
  const lastMonth = lastDate.toPlainYearMonth();
  if (PlainYearMonth.compare(firstMonth, lastMonth) > 0) {
    throw new Error("firstDate must be equal of before lastDate");
  }
  const months: PlainYearMonth[] = [];
  for (
    let month = firstMonth;
    PlainYearMonth.compare(month, lastMonth) <= 0;
    month = month.add({ months: 1 })
  ) {
    months.push(month);
  }
  return months;
};

export const toPlainDateString = (
  date: PlainDate | string
): PlainDateString => {
  if (typeof date === "string") {
    if (!date.match(/^\d{4}-\d{2}-\d{2}$/))
      throw new Error("Invalid date format");
    return date as PlainDateString;
  } else {
    return date.toString() as PlainDateString;
  }
};

export const toPlainYearMonthString = (
  yearMonth: PlainYearMonth | string
): PlainYearMonthString => {
  if (typeof yearMonth === "string") {
    if (!yearMonth.match(/^\d{4}-\d{2}$/))
      throw new Error("Invalid yearMonth format");
    return yearMonth as PlainYearMonthString;
  } else {
    return yearMonth.toString() as PlainYearMonthString;
  }
};

export const toZonedDateTimeString = (
  date: ZonedDateTime
): ZonedDateTimeString => {
  return date.toString() as ZonedDateTimeString;
};

export function dateToInstant(date: Date): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(date.getTime());
}

export function parsePlainDate(data: PlainDateString): PlainDate {
  return PlainDate.from(data);
}

// 様々な形式の日付文字列を YYYY-MM-DD に正規化する
// - スラッシュ区切り: "2024/1/5" → "2024-01-05"
// - ハイフン区切り（月日が1桁）: "2024-1-5" → "2024-01-05"
// - 既に正しい形式: "2024-01-05" → "2024-01-05"
export function parsePlainDateString(date: string): PlainDateString {
  let parsed = date.replace(/\//g, "-");
  parsed = parsed.replace(
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
    (_, y, m, d) => `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
  );
  return parsed as PlainDateString;
}

export function parsePlainYearMonth(
  data: PlainYearMonthString
): PlainYearMonth {
  return PlainYearMonth.from(data);
}

export function parseZonedDateTime(
  data: ZonedDateTimeString
): Temporal.ZonedDateTime {
  return Temporal.ZonedDateTime.from(data);
}

/**
 * タイムゾーンオフセット付き ISO 8601 文字列を Asia/Tokyo の ZonedDateTime に変換する。
 * 外部 API（例: "2026-06-11T08:18:25.000Z", "2026-06-11T18:00:00+09:00"）のパース用
 */
export function parseOffsetDateTime(data: string): ZonedDateTime {
  return Temporal.Instant.from(data).toZonedDateTimeISO(JST_TIME_ZONE);
}

const pad2 = (value: number): string => value.toString().padStart(2, "0");

type FormatPlainTimeInput =
  | { hour: number; minute?: number }
  | PlainDateTime
  | PlainDateTimeString
  | ZonedDateTime
  | ZonedDateTimeString;

/**
 * 時刻を "HH:mm" 形式にフォーマットする
 * @example formatPlainTime({ hour: 9 })                    // "09:00"
 * @example formatPlainTime({ hour: 9, minute: 30 })        // "09:30"
 * @example formatPlainTime(plainDateTime)                  // "14:05"
 * @example formatPlainTime("2026-02-15T14:05:00" as PlainDateTimeString) // "14:05"
 */
export function formatPlainTime(input: FormatPlainTimeInput): string {
  if (typeof input === "string") {
    const parsed = PlainDateTime.from(input);
    return `${pad2(parsed.hour)}:${pad2(parsed.minute)}`;
  }
  return `${pad2(input.hour)}:${pad2(input.minute ?? 0)}`;
}

type FormatPlainDateOptions = {
  excludeYear?: boolean;
  withDayOfWeek?: boolean;
  pad?: boolean;
  /** dot は MAGAZINE の記事日付など、 editorial 表記で使う `2024.09.11` 形式。 */
  format?: "slash" | "ja" | "dot";
};

export function formatPlainDate(
  rawDate: PlainDate | PlainDateString,
  {
    excludeYear,
    withDayOfWeek,
    format = "slash",
    pad = false,
  }: FormatPlainDateOptions = {}
): string {
  const date = typeof rawDate === "string" ? parsePlainDate(rawDate) : rawDate;
  const dayOfWeekChar = ["日", "月", "火", "水", "木", "金", "土", "日"];
  const dayOfWeek = withDayOfWeek ? ` (${dayOfWeekChar[date.dayOfWeek]})` : ``;

  const year = date.year;
  const month = pad ? pad2(date.month) : date.month;
  const day = pad ? pad2(date.day) : date.day;

  if (excludeYear) {
    switch (format) {
      case "ja":
        return `${month}月${day}日${dayOfWeek}`;
      case "slash":
        return `${month}/${day}${dayOfWeek}`;
      case "dot":
        return `${month}.${day}${dayOfWeek}`;
    }
  } else {
    switch (format) {
      case "ja":
        return `${year}年${month}月${day}日${dayOfWeek}`;
      case "slash":
        return `${year}/${month}/${day}${dayOfWeek}`;
      case "dot":
        return `${year}.${month}.${day}${dayOfWeek}`;
    }
  }
}

type FormatPlainYearMonthOptions = {
  format?: "slash" | "ja";
  excludeYear?: boolean;
  pad?: boolean;
};

export function formatPlainYearMonth(
  rawYearMonth: PlainYearMonth | PlainYearMonthString,
  {
    format = "slash",
    excludeYear = false,
    pad = false,
  }: FormatPlainYearMonthOptions = {}
): string {
  const yearMonth =
    typeof rawYearMonth === "string"
      ? parsePlainYearMonth(rawYearMonth)
      : rawYearMonth;
  switch (format) {
    case "ja":
      return (
        (excludeYear ? "" : `${yearMonth.year}年`) +
        `${pad ? pad2(yearMonth.month) : yearMonth.month}月`
      );
    case "slash":
      return `${yearMonth.year}/${Number(yearMonth.month)}`;
  }
}

export function formatZonedDateTimeParts(
  rawDateTime: Temporal.ZonedDateTime | ZonedDateTimeString,
  options: FormatPlainDateOptions = {}
): { datePart: string; timePart: string } {
  const dateTime =
    typeof rawDateTime === "string"
      ? Temporal.ZonedDateTime.from(rawDateTime)
      : rawDateTime;
  const datePart = formatPlainDate(dateTime.toPlainDate(), options);
  const timePart = `${pad2(dateTime.hour)}:${pad2(dateTime.minute)}`;
  return { datePart, timePart };
}

export function formatZonedDateTime(
  rawDateTime: Temporal.ZonedDateTime | ZonedDateTimeString,
  options: FormatPlainDateOptions = {}
): string {
  const { datePart, timePart } = formatZonedDateTimeParts(rawDateTime, options);
  return `${datePart} ${timePart}`;
}

type FormatZonedDateTimeAsDateOptions = FormatPlainDateOptions;

export function formatZonedDateTimeAsDate(
  zonedDateTime: ZonedDateTimeString,
  options: FormatZonedDateTimeAsDateOptions = {}
): string {
  const plainDate = Temporal.ZonedDateTime.from(zonedDateTime).toPlainDate();
  return formatPlainDate(plainDate, options);
}

export function weekFromToday(date: PlainDate): number {
  const dayDiff = getCurrentDate().until(date).days;
  if (dayDiff < 0) {
    return -1;
  }
  return Math.floor(dayDiff / 7) + 1;
}

export function dayOfTheWeek(
  date: PlainDate
):
  | "Holiday"
  | "BeforeHoliday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday" {
  if (isNationalHoliday(date)) {
    return "Holiday";
  }
  if (isNationalHoliday(date.add({ days: 1 }))) {
    return "BeforeHoliday";
  }

  const dayOfWeekChar = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ] as const;

  // date.dayOfWeek is a value between 1 and 7, inclusive, with Monday being 1, and Sunday 7.
  return dayOfWeekChar[date.dayOfWeek - 1]!;
}

export function isNationalHoliday(date: PlainDate): boolean {
  const dateStr = date.toString();
  return nationalHolidaySet.has(dateStr);
}

const nationalHolidaySet: ReadonlySet<string> = new Set([
  "2024-02-23",
  "2024-03-20",
  "2024-04-29",
  "2024-05-03",
  "2024-05-04",
  "2024-05-05",
  "2024-05-06",
  "2024-07-15",
  "2024-08-11",
  "2024-08-12",
  "2024-09-16",
  "2024-09-22",
  "2024-09-23",
  "2024-10-14",
  "2024-11-03",
  "2024-11-04",
  "2024-11-23",
  "2025-01-01",
  "2025-01-13",
  "2025-02-11",
  "2025-02-23",
  "2025-02-24",
  "2025-03-20",
  "2025-04-29",
  "2025-05-03",
  "2025-05-04",
  "2025-05-05",
  "2025-05-06",
  "2025-07-21",
  "2025-08-11",
  "2025-09-15",
  "2025-09-23",
  "2025-10-13",
  "2025-11-03",
  "2025-11-23",
  "2025-11-24",
  "2026-01-01",
  "2026-01-12",
  "2026-02-11",
  "2026-02-23",
  "2026-03-20",
  "2026-04-29",
  "2026-05-03",
  "2026-05-04",
  "2026-05-05",
  "2026-05-06",
  "2026-07-20",
  "2026-08-11",
  "2026-09-21",
  "2026-09-22",
  "2026-09-23",
  "2026-10-12",
  "2026-11-03",
  "2026-11-23",
  "2027-01-01",
  "2027-01-11",
  "2027-02-11",
  "2027-02-23",
  "2027-03-21",
  "2027-03-22",
  "2027-04-29",
  "2027-05-03",
  "2027-05-04",
  "2027-05-05",
  "2027-07-19",
  "2027-08-11",
  "2027-09-20",
  "2027-09-23",
  "2027-10-11",
  "2027-11-03",
  "2027-11-23",
]);

export function isExtraPeakDay(date: PlainDate): boolean {
  const dateStr = date.toString();
  return extraPeakDaysSet.has(dateStr);
}

// 土曜以外の SANU 定義日 (大型連休の中日・祝前日・年末年始等)。
// 2027/4/1 宿泊分から適用される新繁忙日定義の一部で、年度ごとに追記する。
const extraPeakDaysSet: ReadonlySet<string> = new Set([
  // 2026 年度分。cutover (RESERVATION_RULE_REVISION_2027_FIRST_APPLICABLE_NIGHT) を
  // 前倒ししてテストするための dead-by-default データで、isExtraPeakDay は isPeakDay 内で
  // gate 通過後にのみ評価されるため、本番 (cutover=2027/4/1) では評価されない。
  "2026-09-20", // シルバーウィーク: 敬老の日前夜 (日)
  "2026-09-21", // 敬老の日 (月)
  "2026-09-22", // 国民の休日 (火)
  "2026-09-23", // 秋分の日 (水)
  "2026-10-11", // スポーツの日前夜 (日)
  "2026-11-22", // 勤労感謝の日前夜 (日)
  "2026-12-29", // 年末年始 (火)
  "2026-12-30", // 年末年始 (水)
  "2026-12-31", // 大晦日 (木)
  "2027-01-01", // 元旦 (金)
  "2027-01-03", // 年始 (日)
  "2027-01-10", // 成人の日前夜 (日)
  "2027-03-21", // 春分の日 (日)
  // 2027 年度 (2027/4/1 - 2028/3/31)
  "2027-04-29",
  "2027-04-30",
  "2027-05-02",
  "2027-05-03",
  "2027-05-04",
  "2027-05-05",
  "2027-07-18",
  "2027-08-08",
  "2027-08-09",
  "2027-08-10",
  "2027-08-11",
  "2027-08-12",
  "2027-08-13",
  "2027-08-15",
  "2027-09-19",
  "2027-09-20",
  "2027-09-21",
  "2027-09-22",
  "2027-09-23",
  "2027-09-24",
  "2027-09-26",
  "2027-10-10",
  "2027-12-29",
  "2027-12-30",
  "2027-12-31",
  "2028-01-02",
  "2028-01-03",
  "2028-01-09",
  "2028-02-11",
  "2028-03-19",
]);
